import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, 
  Star, 
  Heart, 
  Users, 
  Smile, 
  CheckCircle, 
  Lightbulb, 
  Scale,
  ArrowRight,
  Sparkles,
  Target,
  GraduationCap,
  Zap,
  Briefcase
} from 'lucide-react';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { supabase } from '@/lib/supabase';

// Types
interface Question {
  id: string;
  text: string;
  trait_category: 'HEXACO' | 'OCEAN';
  trait: string;
  facet: string;
  stage_id: number;
  is_reversed: boolean;
}

interface Stage {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  emoji: React.ReactNode;
  color: string;
  questions: Question[];
  insight: {
    type: string;
    title: string;
    description: string;
  }
}

interface PersonalityQuizProps {
  userId: string;
  currentRole?: string;
  targetRole?: string;
  onComplete?: (results: any) => void;
}

// Define themed stages for progressive disclosure
const generateStages = (): Stage[] => {
  const stages: Stage[] = [
    {
      id: 1,
      title: "Work Approach",
      subtitle: "How you tackle projects and challenges",
      description: "This section explores how you approach tasks, organize your work, and solve problems.",
      emoji: <Briefcase className="h-8 w-8 text-blue-500" />,
      color: "from-blue-500 to-cyan-500",
      questions: [], // Will be populated with questions
      insight: {
        type: "work_approach",
        title: "Your Work Style",
        description: "You approach work with a balanced mix of structure and flexibility. You prefer having clear plans but remain adaptable when circumstances change."
      }
    },
    {
      id: 2,
      title: "Social Dynamics",
      subtitle: "How you interact with others",
      description: "This section explores how you connect with teammates, communicate, and navigate social situations.",
      emoji: <Users className="h-8 w-8 text-purple-500" />,
      color: "from-purple-500 to-pink-500",
      questions: [], // Will be populated with questions
      insight: {
        type: "social_dynamics",
        title: "Your Collaboration Style",
        description: "You value authentic connections and tend to be thoughtful in social settings. You balance directness with tact and generally maintain composure in challenging interpersonal situations."
      }
    },
    {
      id: 3,
      title: "Decision Making",
      subtitle: "How you evaluate options and choose directions",
      description: "This section explores your approach to making choices, weighing options, and handling uncertainty.",
      emoji: <Target className="h-8 w-8 text-amber-500" />,
      color: "from-amber-500 to-orange-500",
      questions: [], // Will be populated with questions
      insight: {
        type: "decision_making",
        title: "Your Decision-Making Approach",
        description: "You tend to blend analytical thinking with intuition when making decisions. You consider multiple factors including practical outcomes as well as values and principles."
      }
    },
    {
      id: 4,
      title: "Learning & Growth",
      subtitle: "How you develop skills and adapt",
      description: "This section explores your approach to personal development, learning new skills, and embracing change.",
      emoji: <GraduationCap className="h-8 w-8 text-green-500" />,
      color: "from-green-500 to-emerald-500",
      questions: [], // Will be populated with questions
      insight: {
        type: "learning_growth",
        title: "Your Growth Mindset",
        description: "You demonstrate curiosity and openness to learning. You're generally willing to step outside your comfort zone to develop new skills and perspectives."
      }
    },
    {
      id: 5,
      title: "Stress Response",
      subtitle: "How you handle pressure and challenges",
      description: "This section explores how you respond to stress, manage emotions, and overcome obstacles.",
      emoji: <Heart className="h-8 w-8 text-red-500" />,
      color: "from-red-500 to-rose-500",
      questions: [], // Will be populated with questions
      insight: {
        type: "stress_response",
        title: "Your Resilience Style",
        description: "You demonstrate resilience when facing challenges. While you may experience stress, you generally have effective coping strategies and maintain perspective during difficult situations."
      }
    }
  ];
  
  return stages;
};

// Get trait icon
const getTraitIcon = (trait: string) => {
  const icons: { [key: string]: any } = {
    Honesty_Humility: Scale,
    Emotionality: Heart,
    Extraversion: Users,
    Agreeableness: Smile,
    Conscientiousness: CheckCircle,
    Openness: Lightbulb,
    Neuroticism: Brain
  };
  return icons[trait] || Star;
};

// Main component
export function PersonalityQuiz({ userId, currentRole, targetRole, onComplete }: PersonalityQuizProps) {
  // State
  const [stages, setStages] = useState<Stage[]>([]);
  const [currentStage, setCurrentStage] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [unlockedInsights, setUnlockedInsights] = useState<Array<Stage['insight']>>([]);
  const [showInsight, setShowInsight] = useState(false);
  const [progress, setProgress] = useState(0);
  const [streakCount, setStreakCount] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [personalityTraits, setPersonalityTraits] = useState({
    HEXACO: {},
    OCEAN: {}
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentResponse, setCurrentResponse] = useState<number | null>(null);

  // Initialize stages and fetch questions
  useEffect(() => {
    const initializeQuiz = async () => {
      try {
        // Initialize empty stages
        const generatedStages = generateStages();
        
        // Fetch questions from the database
        const { data: questionData, error } = await supabase
          .from('assessment_questions')
          .select('*')
          .order('created_at');
          
        if (error) {
          console.error('Error fetching questions:', error);
          return;
        }
        
        // Distribute questions to their appropriate stages
        if (questionData) {
          const stagesWithQuestions = generatedStages.map(stage => ({
            ...stage,
            questions: questionData.filter(question => question.stage_id === stage.id)
          }));
          
          setStages(stagesWithQuestions);
        }
        
        setIsInitialized(true);
        
        // Check for existing progress
        loadProgress();
      } catch (error) {
        console.error('Error initializing quiz:', error);
      }
    };
    
    initializeQuiz();
  }, [userId]);

  // Update progress when responses or stages change
  useEffect(() => {
    if (stages.length > 0) {
      const totalQuestions = stages.reduce((acc, stage) => acc + stage.questions.length, 0);
      const completedQuestions = Object.keys(responses).length;
      setProgress((completedQuestions / totalQuestions) * 100);
    }
  }, [responses, stages]);

  // Load saved progress
  const loadProgress = async () => {
    try {
      const { data, error } = await supabase
        .from('personality_assessments')
        .select('*')
        .eq('user_id', userId)
        .single();
        
      if (error) {
        console.log('No existing progress found');
        return;
      }
      
      if (data) {
        if (data.status === 'completed') {
          setShowResults(true);
          return;
        }
        
        setResponses(data.raw_responses || {});
        setCurrentStage(data.current_stage || 1);
        
        if (data.completed_stages && data.completed_stages.length > 0) {
          const insights = [];
          for (const stageId of data.completed_stages) {
            const stage = stages.find(s => s.id === stageId);
            if (stage) {
              insights.push(stage.insight);
            }
          }
          setUnlockedInsights(insights);
        }
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  // Save progress
  const saveProgress = async () => {
    try {
      const scores = calculateCurrentScores();
      
      await supabase
        .from('personality_assessments')
        .upsert({
          user_id: userId,
          status: showResults ? 'completed' : 'in_progress',
          current_stage: currentStage,
          completed_stages: unlockedInsights.map((_, index) => index + 1),
          raw_responses: responses,
          honesty_humility: scores.HEXACO.Honesty_Humility,
          emotionality: scores.HEXACO.Emotionality,
          extraversion: scores.HEXACO.Extraversion,
          agreeableness: scores.HEXACO.Agreeableness,
          conscientiousness: scores.HEXACO.Conscientiousness,
          openness: scores.HEXACO.Openness,
          neuroticism: scores.OCEAN.Neuroticism,
          last_activity_at: new Date(),
          completed_at: showResults ? new Date() : null
        }, {
          onConflict: 'user_id'
        });
        
      console.log('Progress saved');
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  // Helper to get current stage object
  const getCurrentStageObject = (): Stage | undefined => {
    return stages.find(stage => stage.id === currentStage);
  };

  // Helper to get current question
  const getCurrentQuestion = (): Question | undefined => {
    const stage = getCurrentStageObject();
    return stage?.questions[currentQuestionIndex];
  };

  // Calculate current scores based on responses
  const calculateCurrentScores = () => {
    const scores = {
      HEXACO: {
        Honesty_Humility: 0,
        Emotionality: 0,
        Extraversion: 0,
        Agreeableness: 0,
        Conscientiousness: 0,
        Openness: 0
      },
      OCEAN: {
        Openness: 0,
        Conscientiousness: 0,
        Extraversion: 0,
        Agreeableness: 0,
        Neuroticism: 0
      }
    };
    
    const counts = {
      HEXACO: {
        Honesty_Humility: 0,
        Emotionality: 0,
        Extraversion: 0,
        Agreeableness: 0,
        Conscientiousness: 0,
        Openness: 0
      },
      OCEAN: {
        Openness: 0,
        Conscientiousness: 0,
        Extraversion: 0,
        Agreeableness: 0,
        Neuroticism: 0
      }
    };
    
    Object.entries(responses).forEach(([questionId, response]) => {
      const allQuestions = stages.flatMap(stage => stage.questions);
      const question = allQuestions.find(q => q.id === questionId);
      
      if (question) {
        const score = question.is_reversed ? 6 - response : response;
        scores[question.trait_category][question.trait] += score;
        counts[question.trait_category][question.trait]++;
      }
    });
    
    Object.keys(scores.HEXACO).forEach(trait => {
      const count = counts.HEXACO[trait];
      if (count > 0) {
        scores.HEXACO[trait] = parseFloat((scores.HEXACO[trait] / count).toFixed(2));
      }
    });
    
    Object.keys(scores.OCEAN).forEach(trait => {
      const count = counts.OCEAN[trait];
      if (count > 0) {
        scores.OCEAN[trait] = parseFloat((scores.OCEAN[trait] / count).toFixed(2));
      }
    });
    
    return scores;
  };

  // Handle answering a question
  const handleResponse = (value: number) => {
    const question = getCurrentQuestion();
    if (!question) return;
    
    // Set the current response for highlighting the selected button
    setCurrentResponse(value);
    
    // Record the response
    setResponses(prev => ({
      ...prev,
      [question.id]: value
    }));
    
    if (value >= 4) {
      setStreakCount(prev => prev + 1);
    } else {
      setStreakCount(0);
    }
    
    // Use setTimeout to create a small delay before moving to the next question
    // This gives users visual feedback of their selection
    setTimeout(() => {
      if (currentQuestionIndex < getCurrentStageObject()!.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        // Reset the current response when moving to the next question
        setCurrentResponse(null);
      } else {
        setShowInsight(true);
        const stageInsight = getCurrentStageObject()!.insight;
        setUnlockedInsights(prev => [...prev, stageInsight]);
        saveProgress();
      }
    }, 300);
  };

  // Move to next stage
  const handleNextStage = () => {
    if (currentStage < stages.length) {
      setCurrentStage(prev => prev + 1);
      setCurrentQuestionIndex(0);
      setShowInsight(false);
      setCurrentResponse(null);
    } else {
      generateFinalResults();
    }
  };

  // Generate final personality results
  const generateFinalResults = async () => {
    setIsAnalyzing(true);
    
    try {
      const scores = calculateCurrentScores();
      setPersonalityTraits(scores);
      
      if (import.meta.env.VITE_GEMINI_API_KEY) {
        const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
        const model = genAI.getGenerativeModel({ model: "models/gemini-2.0-flash" });
  
        const context = {
          currentRole,
          targetRole,
          personalityTraits: scores,
          responses
        };
  
        const result = await model.generateContent([
          {
            text: `Based on these personality assessments, provide career insights. Return ONLY a JSON object without any markdown formatting or code blocks.

Context:
${JSON.stringify(context, null, 2)}

The response should be a plain JSON object with this structure:
{
  "personalityProfile": {
    "summary": "Brief personality profile summary",
    "strengths": ["strength1", "strength2"],
    "growthAreas": ["area1", "area2"]
  },
  "careerSuggestions": {
    "recommendedPaths": [
      {
        "title": "path title",
        "description": "why this path fits",
        "matchScore": 85
      }
    ],
    "suggestedSkills": ["skill1", "skill2"],
    "nextSteps": ["step1", "step2"]
  }
}`
          }
        ]);
  
        const response = await result.response;
        const text = response.text();
  
        const cleanJson = text
          .replace(/^```json\s*/gim, '')
          .replace(/^```\s*/gim, '')
          .replace(/\s*```$/gim, '')
          .trim();
  
        const insights = JSON.parse(cleanJson);
        
        await saveProgress();
        setShowResults(true);
        
        if (onComplete) {
          onComplete({
            personalityTraits: scores,
            insights
          });
        }
      } else {
        setShowResults(true);
        
        if (onComplete) {
          onComplete({
            personalityTraits: scores,
            insights: {
              personalityProfile: {
                summary: "Your personality profile shows a unique combination of traits that influence how you work and interact with others.",
                strengths: ["Adaptability", "Thoughtful decision-making", "Self-awareness"],
                growthAreas: ["Consider different perspectives", "Balance structure with flexibility"]
              },
              careerSuggestions: {
                recommendedPaths: [
                  {
                    title: "Knowledge Worker",
                    description: "Your profile suggests you'd excel in roles that require critical thinking and problem-solving.",
                    matchScore: 85
                  }
                ],
                suggestedSkills: ["Critical thinking", "Communication", "Adaptability"],
                nextSteps: ["Identify specific roles aligning with your strengths", "Develop skills in your growth areas"]
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Error generating results:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (!isInitialized || stages.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Personality Assessment</CardTitle>
          <CardDescription>Loading your assessment...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-6">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </CardContent>
      </Card>
    );
  }

  if (showResults) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              <CardTitle>Your Personality Profile</CardTitle>
            </div>
            <Badge variant="outline" className="gap-1">
              <Star className="h-3 w-3" />
              Profile Complete
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-medium mb-2">Assessment Complete!</h3>
            <p className="text-muted-foreground mb-6">
              Your comprehensive personality profile is ready to view
            </p>
            <div className="flex justify-center">
              <Button onClick={() => window.location.href = '/profile'} size="lg">
                View Your Full Results
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const stageObj = getCurrentStageObject();
  const question = getCurrentQuestion();

  return (
    <Card className="overflow-hidden">
      <CardHeader className={`bg-gradient-to-r ${stageObj?.color}`}>
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            {stageObj?.emoji}
            <div>
              <CardTitle>{stageObj?.title}</CardTitle>
              <CardDescription className="text-white text-opacity-90">
                {stageObj?.subtitle}
              </CardDescription>
            </div>
          </div>
          <Badge variant="secondary" className="gap-1 bg-white bg-opacity-20 border-none text-white">
            <span>Stage {currentStage} of {stages.length}</span>
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-6">
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-1">
            <span>
              {showInsight 
                ? "Stage complete!" 
                : `Question ${currentQuestionIndex + 1} of ${stageObj?.questions.length}`}
            </span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {streakCount >= 3 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-2 bg-amber-100 text-amber-800 rounded-lg flex items-center justify-center gap-2"
          >
            <Zap className="h-4 w-4" />
            <span className="font-medium">
              {streakCount} answer streak! You're on fire! 🔥
            </span>
          </motion.div>
        )}
        
        <AnimatePresence mode="wait">
          {!showInsight ? (
            <motion.div
              key={`question-${currentQuestionIndex}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="mb-6"
            >
              <div className="rounded-lg border p-6 mb-8">
                <div className="flex items-start gap-3 mb-8">
                  {question?.trait && React.createElement(getTraitIcon(question.trait), { 
                    className: "h-5 w-5 text-primary mt-1" 
                  })}
                  <h3 className="text-xl font-medium">
                    {question?.text}
                  </h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
                    <span>Strongly Disagree</span>
                    <span>Strongly Agree</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        onClick={() => handleResponse(value)}
                        className={`flex-1 h-16 rounded-md flex items-center justify-center text-lg transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50
                          ${value === 1 ? 'bg-red-100 hover:bg-red-200 text-red-700' : ''}
                          ${value === 2 ? 'bg-orange-100 hover:bg-orange-200 text-orange-700' : ''}
                          ${value === 3 ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700' : ''}
                          ${value === 4 ? 'bg-green-100 hover:bg-green-200 text-green-700' : ''}
                          ${value === 5 ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700' : ''}
                          ${currentResponse === value ? 'ring-2 ring-primary ring-opacity-100 shadow-md' : ''}
                        `}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground">{stageObj?.title}</span>
                  <span> • </span>
                  <span>{question?.trait.replace(/_/g, ' ')}</span>
                </div>
                {question?.facet && (
                  <Badge variant="outline">
                    {question.facet.replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="insight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="mb-6"
            >
              <div className="rounded-lg border p-6 mb-8">
                <div className="flex justify-center mb-6">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${stageObj?.color} flex items-center justify-center`}>
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-center mb-2">Insight Unlocked!</h3>
                <h4 className="text-lg font-medium text-center mb-4">{stageObj?.insight.title}</h4>
                <p className="text-center mb-8">{stageObj?.insight.description}</p>
                
                <div className="flex justify-center">
                  <Button 
                    onClick={handleNextStage}
                    size="lg"
                    className={`bg-gradient-to-r ${stageObj?.color} hover:opacity-90 transition-opacity`}
                  >
                    {currentStage < stages.length ? (
                      <>Continue to Next Stage <ArrowRight className="ml-2 h-4 w-4" /></>
                    ) : (
                      <>Complete Assessment <CheckCircle className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground text-center">
                Your insights are saved automatically. You can resume anytime.
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Previously unlocked insights */}
        {unlockedInsights.length > 0 && !showInsight && (
          <div className="mt-8">
            <h3 className="font-medium mb-4">Your Insights So Far</h3>
            <div className="space-y-3">
              {unlockedInsights.map((insight, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="rounded-lg border p-4"
                >
                  <h4 className="font-medium">{insight.title}</h4>
                  <p className="text-sm text-muted-foreground">{insight.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
